/* istanbul ignore file */
import {
  lazy, memo, Suspense, useEffect
} from 'react';

import { CheckBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { useIntercom } from '@zenbusiness/application-commons-intercom';
import {
  Outlet,
  Route,
  Routes,
  useLocation
} from 'react-router-dom';

import ErrorBoundary from '#client/components/ErrorBoundary';
import Error from '#client/components/ErrorBoundary/Error';
import Loading from '#client/components/LoadingV2';
import ConditionalTemplate from '#client/components/Template';
import {
  FEDERATED, ROUTES, DEFAULT_SEGMENT, DEPLOY_ENV
} from '#client/manifest';
import Dashboard from '#client/pages/Dashboard';
import moneyRoutes from '#client/pages/Money/routes';
import websitesRoutes from '#client/pages/Websites';
import timeout from '#client/utils/timeout';

import Authenticated from './Authenticated';
import SmartNavigate from './SmartNavigate';

const Callback = lazy(() => timeout(import(/* webpackChunkName: 'callback' */'#client/pages/Callback')));
const Logout = lazy(() => timeout(import(/* webpackChunkName: 'logout' */'#client/pages/Logout')));

const Accounting = lazy(() => timeout(import(/* webpackChunkName: 'accounting' */'#client/pages/Accounting')));
const AccountNotFound = lazy(() => timeout(import(/* webpackChunkName: 'accountNotFound' */'#client/pages/AccountNotFound')));
const AccountSettings = lazy(() => timeout(import(/* webpackChunkName: 'account' */'#client/pages/AccountSettings')));
const Amendments = lazy(() => timeout(import(/* webpackChunkName: 'amendments' */'#client/pages/Amendments')));
const AnnualReports = lazy(() => timeout(import(/* webpackChunkName: 'annualReports' */'#client/pages/AnnualReports')));
const Banking = lazy(() => timeout(import(/* webpackChunkName: 'banking' */'#client/pages/Banking')));
const BankOfAmericaOffer = lazy(() => timeout(import(/* webpackChunkName: 'BankOfAmericaOffer' */'#client/pages/BankOfAmericaOffer')));
const BusinessHome = lazy(() => timeout(import(/* webpackChunkname: 'company' */'#client/pages/Home')));
const BusinessOverview = lazy(() => timeout(import(/* webpackChunkname: 'company' */'#client/pages/BusinessOverview')));
const BusinessProfile = lazy(() => timeout(import(/* webpackChunkName: 'company' */'#client/pages/BusinessProfile')));
const Download = lazy(() => timeout(import(/* webpackChunkName: 'download' */'#client/pages/Download')));
const EducationStudio = lazy(() => timeout(import(/* webpackChunkName: 'education' */'#client/pages/EducationStudio')));
const EducationStudioLesson = lazy(() => timeout(import(/* webpackChunkName: 'education' */'#client/pages/EducationStudio/components/LessonViewer/LessonViewerWrapper')));
const EducationStudioSeries = lazy(() => timeout(import(/* webpackChunkName: 'education' */'#client/pages/EducationStudio/ArticleViewer')));
const Expenses = lazy(() => timeout(import(/* webpackChunkName: 'expenseTracker' */'#client/pages/ExpenseTracker')));
const ExpenseTracker = lazy(() => timeout(import(/* webpackChunkName: 'expenseTracker' */'#client/pages/ExpenseTracker')));
const FinancialReadiness = lazy(() => timeout(import(/* webpackChunkName: 'financialReadiness' */'#client/pages/FinancialReadiness')));
const Help = lazy(() => timeout(import(/* webpackChunkName: 'help' */'#client/pages/Help')));
const Income = lazy(() => timeout(import(/* webpackChunkName: 'expenseTracker' */'#client/pages/ExpenseTracker')));
const MarketingToolsLayout = lazy(() => timeout(import(/* webpackChunkName: 'MarketingTools' */'#client/pages/MarketingTools/MarketingToolsLayout')));
const MktgProductRedirect = lazy(() => timeout(import(/* webpackChunkName: 'cma' */'#client/pages/MktgProductRedirect')));
const MobileAccountNotFound = lazy(() => timeout(import(/* webpackChunkName: 'mobileAccountNotFound' */'#client/pages/MobileAccountNotFound')));
const Orders = lazy(() => timeout(import(/* webpackChunkName: 'orders' */'#client/pages/Orders')));
const OrderView = lazy(() => timeout(import(/* webpackChunkName: 'orders' */'#client/pages/OrderView')));
const Payments = lazy(() => timeout(import(/* webpackChunkName: 'payments' */'#client/pages/Payments')));
const Taxes = lazy(() => timeout(import(/* webpackChunkName: 'expenseTracker' */'#client/pages/ExpenseTracker')));
const VirtualBusinessAddress = lazy(() => timeout(import(/* webpackChunkName: 'vba' */'#client/pages/VirtualBusinessAddress')));
const WorryFreeCompliance = lazy(() => timeout(import(/* webpackChunkName: 'wfc' */'#client/pages/WorryFreeCompliance')));
const TaxServicePage = lazy(() => timeout(import(/* webpackChunkName: 'wfc' */'#client/pages/TaxServices')));

const RouteSuspense = memo(() => (
  <ErrorBoundary>
    <Suspense fallback={<Loading />}>
      <Outlet />
    </Suspense>
  </ErrorBoundary>
));
RouteSuspense.displayName = 'RouteSuspense';

const fedProps = {
  loadingElement: <Loading />,
  errorElement: <Error />
};

function BusinessEntityGate() {
  return (
    <CheckBusinessEntity loadingElement={<Loading />}>
      <Outlet />
    </CheckBusinessEntity>
  );
}

function educationStudioRoutes(shouldUseMfe) {
  return shouldUseMfe
    ? (
      <Route path="education-studio" element={<Outlet />}>
        <Route index element={<FederatedModule {...fedProps} {...FEDERATED.EducationUi} exportName="EducationStudio" />} />
        <Route path="lessons/:lesson/:step" element={<FederatedModule {...fedProps} {...FEDERATED.EducationUi} exportName="EducationStudioLesson" />} />
        <Route path=":series/:article" element={<FederatedModule {...fedProps} {...FEDERATED.EducationUi} exportName="EducationStudioArticle" />} />
        <Route path="*" element={<SmartNavigate replace to={ROUTES.educationStudio} />} />
      </Route>
    )
    : (
      <Route path="education-studio" element={<Outlet />}>
        <Route index element={<EducationStudio />} />
        <Route path="lessons/:lesson/:step" element={<EducationStudioLesson />} />
        <Route path=":series/:article" element={<EducationStudioSeries />} />

        <Route path="*" element={<SmartNavigate replace to={ROUTES.educationStudio} />} />
      </Route>
    );
}

function ZenRouter() {
  const { pathname } = useLocation();
  const { update: updateIntercom } = useIntercom();
  const enableMoneyUIMFE = useExperimentFlag('enable-money-ui-mfe-on-zendash', false);
  const enableInsightsUIMFE = useExperimentFlag('enable-insights-ui-mfe', false);
  const taxServicesFlag = useExperimentFlag('enable-tax-services', false);
  const enableWebsitesUIMFE = useExperimentFlag('enable-websites-ui-mfe', 0);
  const enableEducationUiMfe = useExperimentFlag('enable-education-ui-mfe', false);

  useEffect(() => {
    if (pathname === '/d/callback') {
      return;
    }
    updateIntercom({ hide_default_launcher: true });
  }, [pathname, updateIntercom]);
  return (
    <Routes>
      {/* REDIRECTS ---------------------------------------------------------------------- */}
      <Route
        path="/d/company/:businessEntityUuid/details"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/business-profile" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/businessGuides"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/business-guides" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/documents"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/business-records" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/businessRecords"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/business-records" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/documentTemplates"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/document-templates" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/myUploads"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/my-uploads" />}
      />
      {/* Redirects to logout -> universal login for password reset flow */}
      <Route
        path="/d/password-reset"
        element={<SmartNavigate replace to="/d/logout" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/vba"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/virtual-business-address" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/journey-to-success"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/education-studio" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/journey-to-success/lessons/:lesson/:step"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/education-studio/lessons/:lesson/:step"
          />
        )}
      />

      {/* For backwards compatibility redirect previous domain related paths to new ones */}
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid/contacts/:domainContactUuid"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid/contacts/:domainContactUuid"
          />
        )}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid/dns/:domainDnsUuid"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid/dns/:domainDnsUuid"
          />
        )}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid/dns"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid/dns"
          />
        )}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid/external-records"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid/external-records"
          />
        )}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid/external-verify"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid/external-verify"
          />
        )}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid/nameservers"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid/nameservers"
          />
        )}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid/privacy"
        element={(
          <SmartNavigate
            replace
            to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid/privacy"
          />
        )}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains/:domainUuid"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/marketing-tools/domains/:domainUuid" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/domains"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/marketing-tools/domains" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/external-domains"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/marketing-tools/external-domains" />}
      />
      <Route
        path="/d/company/:businessEntityUuid/websites"
        element={<SmartNavigate replace to="/d/company/:businessEntityUuid/marketing-tools/websites" />}
      />
      <Route
        path={ROUTES.unsupported}
        element={<SmartNavigate replace to={ROUTES.businessOverview} />}
      />
      {/* UNAUTHENTICATED ---------------------------------------------------------------------- */}

      <Route path={DEFAULT_SEGMENT} element={<ConditionalTemplate />}>
        <Route element={<RouteSuspense />}>

          <Route path="logout" element={<Logout />} />
          <Route path="callback" element={<Callback />} />
          <Route path="account-not-found" exact element={<AccountNotFound />} />
          <Route path="account-not-found-mobile" exact element={<MobileAccountNotFound />} />

          {/* AUTHENTICATED */}
          <Route element={<Authenticated />}>
            {/* Marketing Redirect
            ---------------------------------------------------------- */}
            <Route path="marketing/:redirectTo" element={<Dashboard />} />

            <Route path="dashboard" exact element={<Dashboard />} />
            <Route path="download" exact element={<Download />} />
            <Route path="financialreadiness" exact element={<FinancialReadiness />} />
            <Route path="help" exact element={<Help />} />

            {/* /d/account/* Pages */}
            <Route path="account" element={<Outlet />}>
              <Route path="payments" element={<Payments />} />
              <Route path="settings" element={<AccountSettings />} />
              <Route index path="*" element={<SmartNavigate replace to={ROUTES.accountSettings} />} />
            </Route>

            {/* /d/company/* */}
            <Route path="company" element={<Outlet />}>
              <Route index element={<BusinessOverview />} />

              {/* /d/company/:businessEntityUuid/* */}
              <Route path=":businessEntityUuid" element={<BusinessEntityGate />}>
                <Route index element={<BusinessHome />} />
                <Route path="accounting" element={<Accounting />} />
                <Route path="amendments" element={<Amendments />} />
                <Route path="annual-reports" element={<AnnualReports />} />
                <Route path="bank-of-america-offer" element={<BankOfAmericaOffer />} />
                <Route path="banking" element={<Banking />} />
                <Route path="business-profile" element={<BusinessProfile />} />
                <Route path="buy/ureeka" element={<MktgProductRedirect />} />
                {taxServicesFlag && <Route path="tax-services" element={<TaxServicePage />} />}
                {/* /d/company/:businessEntityUuid/education-studio/* */}
                {educationStudioRoutes(enableEducationUiMfe)}

                <Route path="expenses" element={<Expenses />} />
                <Route path="expense-tracker" element={<ExpenseTracker />} />

                <Route path="income" element={<Income />} />

                {/* /d/company/:businessEntityUuid/marketing-tools/* */}
                <Route path="marketing-tools" element={<MarketingToolsLayout />}>
                  {websitesRoutes(enableWebsitesUIMFE)}
                  <Route path="*" element={<SmartNavigate replace to={ROUTES.marketingTools} />} />
                </Route>

                {/* /d/company/:businessEntityUuid/money/* */}
                {moneyRoutes(enableMoneyUIMFE)}

                {/* /d/company/:businessEntityUuid/orders/* */}
                <Route path="orders" element={<Outlet />}>
                  <Route index element={<Orders />} />
                  <Route path=":orderTaskUuid" element={<OrderView />} />

                  <Route path="*" element={<SmartNavigate replace to={ROUTES.orders} />} />
                </Route>

                <Route path="taxes" element={<Taxes />} />
                <Route path="virtual-business-address" element={<VirtualBusinessAddress />} />
                <Route path="worry-free-compliance" element={<WorryFreeCompliance />} />

                <Route path="business-records/*" element={<FederatedModule {...fedProps} {...FEDERATED.DocumentsUi} exportName="BusinessRecords" />} />
                <Route path="document-templates/*" element={<FederatedModule {...fedProps} {...FEDERATED.DocumentsUi} exportName="DocumentTemplates" />} />
              </Route>
            </Route>
            <Route path="e/*" element={<FederatedModule {...fedProps} {...FEDERATED.EcommServicesUi} />} />
            <Route path="b/*" element={<FederatedModule {...fedProps} {...FEDERATED.bankingUI} />} />
            {enableInsightsUIMFE && <Route path="i/*" element={<FederatedModule {...fedProps} {...FEDERATED.InsightsUi} />} />}
            {DEPLOY_ENV !== 'production' && (/* THIS IS FOR FEDERATION DEBUGGING PURPOSES */
              <Route path="x/*" element={<FederatedModule {...fedProps} {...FEDERATED.AppTemplateUi} />} />
            )}
          </Route>
        </Route>
      </Route>
      <Route path="/d/" element={<SmartNavigate replace to={ROUTES.dashboard} />} />
      <Route path="*" element={<SmartNavigate replace to={ROUTES.dashboard} />} />
    </Routes>
  );
}

export default ZenRouter;
